import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { usePublicData } from "@/hooks/usePublicData";

const API = `${window.location.origin}/api`;
const SITE_ORIGIN = "https://sustainablehandymanservices.uk";

const prettyCategory = (c) => (c || "").replace(/_/g, " ").replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const absoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${window.location.origin}${url}`;
};


export default function GalleryDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const { bizInfo, services } = usePublicData();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setActivePhoto(0);
    axios.get(`${API}/gallery/slug/${encodeURIComponent(slug)}`).then((res) => {
      if (cancelled) return;
      setItem(res.data?.item || null);
      setRelated(res.data?.related || []);
    }).catch((e) => {
      if (cancelled) return;
      if (e.response?.status === 404) setNotFound(true);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  // Scroll to top when the slug changes so a freshly opened project starts at the hero.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [slug]);

  // Compute SEO metadata (uses safe fallbacks while item is loading / missing)
  const heroPhoto = item ? absoluteUrl(item.photos?.[activePhoto] || item.photos?.[0]) : null;
  const canonical = item ? `${SITE_ORIGIN}/gallery/${item.slug}` : `${SITE_ORIGIN}/gallery/${slug}`;
  const metaDesc = item
    ? (item.meta_description || `${item.title} — a completed project by Sustainable Handyman Services in Abergavenny.`)
    : "Sustainable Handyman Services project.";

  // JSON-LD CreativeWork schema — gives Google rich image + breadcrumb context for local SEO.
  const jsonLd = item ? {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": item.title,
    "description": metaDesc,
    "image": (item.photos || []).map(absoluteUrl).filter(Boolean),
    "url": canonical,
    "creator": {
      "@type": "LocalBusiness",
      "name": "Sustainable Handyman Services",
      "address": { "@type": "PostalAddress", "addressLocality": "Abergavenny", "addressRegion": "Monmouthshire", "addressCountry": "GB" },
    },
    "datePublished": item.published_at,
  } : null;

  // Direct-DOM SEO sync (Helmet in our dev-preview build reliably updates only <title>).
  // Called unconditionally so hook order stays stable regardless of loading/404 state.
  useSeoHeadTags({
    title: notFound
      ? "Project not found | Sustainable Handyman Services"
      : item
        ? `${item.title} | Sustainable Handyman Services`
        : "Loading project | Sustainable Handyman Services",
    description: notFound ? "This project may have been moved or unpublished." : metaDesc,
    canonical: item ? canonical : null,
    ogImage: heroPhoto,
    ogType: "article",
    noindex: notFound,
    jsonLd,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center gap-4">
        <ImageIcon className="h-10 w-10 text-muted-foreground opacity-40" />
        <h1 className="text-2xl font-bold font-heading">Project not found</h1>
        <p className="text-muted-foreground">This project may have been moved or unpublished.</p>
        <Button onClick={() => navigate("/gallery")} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to the gallery
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav />

      {/* Breadcrumb */}
      <section className="pt-24 pb-2 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="text-xs text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/gallery" className="hover:text-primary">Gallery</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground truncate">{item.title}</span>
        </nav>
      </section>

      {/* Hero + photos */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="mb-4">
          <Badge variant="outline" className="capitalize mb-2" data-testid="gallery-detail-category">{prettyCategory(item.category)}</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading tracking-tight" data-testid="gallery-detail-title">{item.title}</h1>
        </div>

        {/* Hero photo with arrows */}
        {heroPhoto && (
          <div className="relative rounded-xl overflow-hidden bg-muted" data-testid="gallery-detail-hero">
            <img
              src={heroPhoto}
              alt={item.title}
              className="w-full max-h-[560px] object-cover"
              width="1200"
              height="800"
              fetchpriority="high"
            />
            {item.photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setActivePhoto((p) => (p - 1 + item.photos.length) % item.photos.length)}
                  className="absolute top-1/2 -translate-y-1/2 left-3 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                  aria-label="Previous photo"
                  data-testid="gallery-detail-prev-photo"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setActivePhoto((p) => (p + 1) % item.photos.length)}
                  className="absolute top-1/2 -translate-y-1/2 right-3 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                  aria-label="Next photo"
                  data-testid="gallery-detail-next-photo"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                  {activePhoto + 1} / {item.photos.length}
                </div>
              </>
            )}
          </div>
        )}

        {/* Thumbnail strip */}
        {item.photos.length > 1 && (
          <div className="mt-3 grid grid-cols-5 sm:grid-cols-8 gap-2" data-testid="gallery-detail-thumbs">
            {item.photos.map((url, idx) => (
              <button
                key={url}
                type="button"
                onClick={() => setActivePhoto(idx)}
                className={`aspect-square rounded overflow-hidden border-2 transition-colors ${activePhoto === idx ? "border-primary" : "border-transparent hover:border-muted-foreground/40"}`}
                aria-label={`View photo ${idx + 1}`}
              >
                <img src={absoluteUrl(url)} alt="" className="w-full h-full object-cover" loading="lazy" width="200" height="200" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Description */}
      {item.description && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div
            className="prose prose-lg max-w-none"
            data-testid="gallery-detail-description"
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        </section>
      )}

      {/* CTA */}
      <section className="bg-primary/5 py-10 px-4 sm:px-6 lg:px-8 mt-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl font-bold font-heading mb-2">Like this project?</h2>
          <p className="text-muted-foreground mb-4">We'd love to build something similar for you. Send us a quick message — no obligation.</p>
          <Link to="/enquiry">
            <Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
              Start a similar project
            </Button>
          </Link>
        </div>
      </section>

      {/* Related projects */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="gallery-detail-related">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-bold font-heading">Related projects</h2>
            <Link to="/gallery" className="text-sm text-primary inline-flex items-center gap-1 hover:underline">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {related.map((r) => {
              const cover = absoluteUrl(r.photos?.[0]);
              return (
                <Link key={r.id} to={`/gallery/${r.slug}`} className="group" data-testid={`gallery-detail-related-card-${r.slug}`}>
                  <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted mb-2">
                    {cover ? (
                      <img src={cover} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" width="800" height="600" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  <p className="font-medium group-hover:text-primary transition-colors">{r.title}</p>
                  <p className="text-xs text-muted-foreground capitalize">{prettyCategory(r.category)}</p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <PublicFooter bizInfo={bizInfo} services={services} />
    </div>
  );
}
